import {
  PermissionCategory,
  type Permission,
  PermissionProviderCategory,
  PermissionType,
} from '@repo/api-gw-sdk';

export type PermissionDescriptor = Permission & {
  group?: string;
};

export const ManageBackupSettingsGroup = 'Manage backup settings';

const SearchWorkspaceDataGroup = 'Search and explore workspace data';
const RestoreWorkspaceDataGroup = 'Restore workspace data';

const permissions: Record<PermissionType, { group?: string }> = {
  [PermissionType.GlobalSettingsView]: {},
  [PermissionType.VaultAccountsManage]: {},
  [PermissionType.CostExplorerView]: {},
  [PermissionType.AuditLogsView]: {},
  [PermissionType.UserAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.SamlGroupAccessManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsManage]: {
    group: 'Access management',
  },
  [PermissionType.ApiCredentialsRotateSecret]: {
    group: 'Access management',
  },
  [PermissionType.CustomRolesManage]: {
    group: 'Access management',
  },
  [PermissionType.OauthAppsManage]: {
    group: 'Integrations',
  },
  [PermissionType.NotificationPoliciesManage]: {
    group: 'Integrations',
  },
  [PermissionType.PermissionIdUnspecified]: {},
  [PermissionType.DashboardView]: {},
  [PermissionType.SaasDashboardView]: {},
  [PermissionType.InventoryView]: {},
  [PermissionType.SaasInventoryView]: {},
  [PermissionType.JobsView]: {},
  [PermissionType.SaasJobsView]: {},
  [PermissionType.BackupSettingsView]: {},
  [PermissionType.ThreatDetectionCenterView]: {},
  [PermissionType.StorageGatewayView]: {},
  [PermissionType.DatabaseExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.FileExplorerSearch]: {
    group: 'Search and explore',
  },
  [PermissionType.SaasMailSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupDataSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.SaasUserSearch]: {
    group: SearchWorkspaceDataGroup,
  },
  [PermissionType.ThreatDetectionExclusionManage]: {},
  [PermissionType.FileExplorerDelete]: {},
  [PermissionType.StorageGatewayDataDownload]: {},
  [PermissionType.StorageGatewayMetadataView]: {},
  [PermissionType.DataCatalogView]: {},
  [PermissionType.DataCatalogQuery]: {},
  [PermissionType.SnapshotsTakeOrConvert]: {},
  [PermissionType.SaasSnapshotsTake]: {},
  [PermissionType.ResourcesRestore]: {},
  [PermissionType.ConfigurationValidate]: {},
  [PermissionType.SaasConfigurationValidate]: {},
  [PermissionType.DiscoveryTrigger]: {},
  [PermissionType.SaasDiscoveryTrigger]: {},
  [PermissionType.FilesRestore]: {
    group: 'Restore granular data',
  },
  [PermissionType.SaasMailRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasGroupRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasSharedDriveRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.SaasUserRestore]: {
    group: RestoreWorkspaceDataGroup,
  },
  [PermissionType.DatabasesQuery]: {
    group: 'Restore granular data',
  },
  [PermissionType.DataClassesOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.EnvironmentsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.DetectedAppsOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.SensitivityLevelOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.NetworkClassificationOverride]: {
    group: 'Override classifications',
  },
  [PermissionType.ControlViolationsMute]: {},
  [PermissionType.BackupExclusionManage]: {},
  [PermissionType.VaultsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasSourceAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RestoreAccountsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.DiscoveryConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.BackupConfigurationManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ZeroEtlPoliciesManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.ControlsManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.RansomwareDetectionManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.SaasBackupScopeManage]: {
    group: ManageBackupSettingsGroup,
  },
  [PermissionType.StorageGatewayManage]: {
    group: 'Storage gateway',
  },
  [PermissionType.ActionApprovalRulesManage]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRulesView]: {
    group: 'Access management',
  },
  [PermissionType.ActionApprovalRequestsManage]: {},
  [PermissionType.MpaPoliciesManage]: {
    group: 'Access management',
  },
  [PermissionType.MpaPoliciesView]: {
    group: 'Access management',
  },
  [PermissionType.MpaRequestsManage]: {},
  [PermissionType.RestoreTemplatesManage]: {},
  [PermissionType.DatalakeView]: {
    group: 'Managed data lake',
  },
  [PermissionType.DatalakeOperate]: {
    group: 'Managed data lake',
  },
  [PermissionType.ComplianceView]: {},
  [PermissionType.ClassificationPoliciesManage]: {},
};

export const Permissions = Object.fromEntries(
  Object.entries(permissions).filter(
    ([key]) => key !== PermissionType.PermissionIdUnspecified
  )
);

const permissionCategories: Record<PermissionCategory, { title: string }> = {
  [PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION]: {
    title: 'Administration',
  },
  [PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT]: {
    title: 'Global management',
  },
  [PermissionCategory.PERMISSION_CATEGORY_OPERATION]: {
    title: 'Operation',
  },
  [PermissionCategory.PERMISSION_CATEGORY_VIEWING]: {
    title: 'Viewing',
  },
};

export const PermissionCategories = Object.fromEntries(
  Object.entries(permissionCategories)
);

export enum PermissionFamily {
  GlobalManagement = 'GLOBAL_MANAGEMENT',
  Cloud = 'CLOUD',
  Saas = 'SAAS',
  StorageGateway = 'STORAGE_GATEWAY',
}

const providerCategoryFamilies: Record<
  PermissionProviderCategory,
  PermissionFamily
> = {
  [PermissionProviderCategory.PERMISSION_PROVIDER_CATEGORY_CLOUD_PLATFORM]:
    PermissionFamily.Cloud,
  [PermissionProviderCategory.PERMISSION_PROVIDER_CATEGORY_SAAS]:
    PermissionFamily.Saas,
  [PermissionProviderCategory.PERMISSION_PROVIDER_CATEGORY_STORAGE_GATEWAY]:
    PermissionFamily.StorageGateway,
};

const providerFamilyOrder = [
  PermissionFamily.Cloud,
  PermissionFamily.Saas,
  PermissionFamily.StorageGateway,
];

// Global-management permissions carry PERMISSION_CATEGORY_GLOBAL_MANAGEMENT rather than one of
// Viewing/Operation/Administration, so once "Global management" becomes a family chip they have
// no section to sit in. Reclassifying them in the catalog is not an option: projectAdminCategories
// derives the built-in Admin, Operator and Viewer roles from [VIEWING, OPERATION, ADMINISTRATION],
// so moving these would silently grant those roles user-access and custom-role management.
const globalManagementSections: Record<string, PermissionCategory> = {
  [PermissionType.GlobalSettingsView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.AuditLogsView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.CostExplorerView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.ActionApprovalRulesView]:
    PermissionCategory.PERMISSION_CATEGORY_VIEWING,
  [PermissionType.VaultAccountsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.UserAccessManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.SamlGroupAccessManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ApiCredentialsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ApiCredentialsRotateSecret]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.CustomRolesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.ActionApprovalRulesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.NotificationPoliciesManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
  [PermissionType.OauthAppsManage]:
    PermissionCategory.PERMISSION_CATEGORY_ADMINISTRATION,
};

const familyViewGroups: Record<string, string> = {
  [PermissionType.VaultAccountsManage]: 'Vault accounts',
  [PermissionType.ControlViolationsMute]: 'Resource exceptions',
  [PermissionType.BackupExclusionManage]: 'Resource exceptions',
  [PermissionType.BackupSettingsView]: 'Backup settings',
  [PermissionType.ComplianceView]: 'Backup settings',
  [PermissionType.ClassificationPoliciesManage]: ManageBackupSettingsGroup,
  [PermissionType.RestoreTemplatesManage]: ManageBackupSettingsGroup,
};

export const getPermissionFamilies = (
  permission: PermissionDescriptor
): PermissionFamily[] => {
  if (
    permission.category ===
    PermissionCategory.PERMISSION_CATEGORY_GLOBAL_MANAGEMENT
  ) {
    return [PermissionFamily.GlobalManagement];
  }

  const declared = permission.providerCategories || [];
  // An empty list means unclassified, which the catalog reads as applying to every provider —
  // so it must show under the provider chips rather than vanishing from all of them. The gateway
  // is a surface a permission has to opt into, so unclassified stays out of it.
  if (declared.length === 0) {
    return [PermissionFamily.Cloud, PermissionFamily.Saas];
  }

  return providerFamilyOrder.filter((family) =>
    declared.some((category) => providerCategoryFamilies[category] === family)
  );
};

// A permission the gateway shares with another provider still has to reach that provider's
// surface, so only the gateway-exclusive ones follow the gateway's availability.
export const isStorageGatewayOnly = (permission: PermissionDescriptor) => {
  const families = getPermissionFamilies(permission);

  return (
    families.length > 0 &&
    families.every((family) => family === PermissionFamily.StorageGateway)
  );
};

export const getPermissionSection = (
  permission: PermissionDescriptor
): PermissionCategory =>
  globalManagementSections[permission.permissionType] || permission.category;

export const getPermissionFamilyGroup = (permission: PermissionDescriptor) =>
  permission.permissionType in familyViewGroups
    ? familyViewGroups[permission.permissionType]
    : permission.group;
